import * as React from 'react';
import * as _ from 'underscore';

import {
  DropdownButtonType,
  SingleSelectDropdown,
  TabStyles
} from '@tableau/widgets';

import { Messages } from '../../codegen/Localize';
import { angularProviderHOC } from '../../common/react/AngularProviderHOC';
import { Location } from '../../common/react/Location';
import { FavoritesMenuNavigateToFavoriteEvent, FavoritesMenuOpenEvent, TelemetryEvents } from '../../common/telemetryEvents/TelemetryEvents';
import { imgPath } from '../../common/utilities/ImgUrl';
import { Favorites } from '../../ts/resources/Favorites';
import { IFavorite } from '../../ts/ServerApi/types/IFavorite';
import { Telemetry } from '../../ts/services/TelemetryService';
import { ServiceLocator } from '../ServiceLocator';
import { SpacesSiteUrls } from '../spaces/SpacesSiteUrls';
import { FavoritesList, getFavoriteKey } from './FavoritesList';
import { HeaderStyles } from './HeaderStyles';

// we just want to grab all favorites, we usually use 1000 as max number of items to fetch
const PAGESIZE_MAX_SLICE: number = 1000;

export interface FavoritesDropdownOwnProps {
  readonly siteUrls: SpacesSiteUrls;
}

export interface FavoritesDropdownPropsFromAngular {
  readonly favoritesService: Favorites;
  readonly telemetry: Telemetry;
}

export type FavoritesDropdownProps = FavoritesDropdownOwnProps & FavoritesDropdownPropsFromAngular;

export interface FavoritesDropdownState {
  readonly favorites: IFavorite[];
  readonly favoritesRequestComplete: boolean;
  readonly buttonHover: boolean;
  readonly panelOpened: boolean;
}

export class FavoritesDropdown extends React.Component<FavoritesDropdownProps, FavoritesDropdownState> {

  public constructor(props: FavoritesDropdownProps) {
    super(props);
    this.state = {favorites: [], favoritesRequestComplete: false, buttonHover: false, panelOpened: false};
  }

  public render(): JSX.Element {
    return (
      <div style={DropdownContainerStyle}>
        <div onMouseEnter={this.onMouseEnter} onMouseLeave={this.onMouseLeave}>
          <SingleSelectDropdown
            children={this.getButtonContent()}
            type={DropdownButtonType.Text}
            list={this.getList()}
            value={''}
            testId="favorites-menu"
            onOpen={this.handleOpen}
            onClose={this.handleClose}
            onChange={this.handleChange}
            useDarkStyling={true}
            label={''}
            aria-controls={'favorites-menu-popup'}
          />
          </div>
      </div>
    );
  }

  private getList = () => {
    return (
      <FavoritesList
        id={'favorites-menu-popup'}
        onSelect={this.handleChange}
        urlForFavorite={this.urlForFavorite}
        items={this.state.favorites}
        favoritesRequestCompleted={this.state.favoritesRequestComplete} />
    );
  }

  private closePanel: () => void;

  private handleOpen = (done: () => void): void => {
    // save close panel for later
    this.closePanel = done;
    window.addEventListener('blur', this.onWindowBlur);

    // 2019.4 Metrics -- return all favorites except for metrics to not show metrics on the dropdown menu
    this.props.favoritesService.allButMetrics().getSlice(0, PAGESIZE_MAX_SLICE)
      .then((result) => {
        this.props.telemetry.sendEvent<FavoritesMenuOpenEvent>({
          type: TelemetryEvents.FavoritesMenuOpen,
          favoriteItemCount: result.total,
        });
        this.setState({favorites: result.items, favoritesRequestComplete: true, panelOpened: true});
      }).catch(() => {
        this.setState({favoritesRequestComplete: true, panelOpened: true});
      });
  }

  private handleClose = (): void => {
    if (this.closePanel) {
      this.closePanel();
      this.closePanel = null;
      this.setState({panelOpened: false});
    }
  }

  private onWindowBlur = (): void => {
    this.handleClose();
    window.removeEventListener('blur', this.onWindowBlur);
  }

  private urlForFavorite = (favorite: IFavorite): string => {
    let redirectUrl = '';
    switch(favorite.objectType) {
      case 'datasource':
        if (favorite.objectId === '') {
          redirectUrl = this.props.siteUrls.favoriteDatasourceDoesntExistUrl(favorite.objectIdForFavoriting);
        } else  {
          redirectUrl = this.props.siteUrls.favoriteDatasourceUrl(favorite.objectId, favorite.objectIdForFavoriting);
        }
        break;

      case 'project':
        redirectUrl = this.props.siteUrls.favoriteProjectUrl(favorite.objectId);
        break;

      case 'workbook':
        redirectUrl = this.props.siteUrls.favoriteWorkbookUrl(favorite.objectId);
        break;

      case 'view':
        redirectUrl = this.props.siteUrls.redirectToFavoriteViewUrl(favorite.objectId);
        break;

      case 'flow':
        redirectUrl = this.props.siteUrls.favoriteFlowUrl(favorite.objectId);
        break;

      case 'dataRole':
        redirectUrl = this.props.siteUrls.redirectToDataRoleUrl(favorite.objectId);
        break;

      // 2019.4 Metrics
      case 'metric': // TODO: We will need the luid for a metric (TFS 950796)
        redirectUrl = this.props.siteUrls.metricUrl(favorite.objectId);  // TODO: Add redirect for zombie metrics
        break;
    }

    return redirectUrl;
  }

  private handleChange = (value: string) => {
    const favorite = this.state.favorites.find((favorite) => {
      return getFavoriteKey(favorite) === value;
    });

    if (!favorite) {
      return;
    }

    const redirectUrl = this.urlForFavorite(favorite);
    if (redirectUrl) {
      this.props.telemetry.sendEvent<FavoritesMenuNavigateToFavoriteEvent>({
        type: TelemetryEvents.FavoritesMenuNavigateToFavorite,
        favoriteItemType: favorite.objectType,
        favoriteItemId: favorite.objectIdForFavoriting, // TODO: once unified data sources have been sorted out, change this to objectId
      });
      Location.instance.setUrl(redirectUrl);
    }

    this.handleClose();
  }

  private onMouseEnter = () => {
    this.setState({buttonHover: true});
  }

  private onMouseLeave = () => {
    this.setState({buttonHover: false});
  }

  private getButtonContent = () => {
    const active = this.state.panelOpened || this.state.buttonHover;
    const imgSrc = active
      ? imgPath('spaces_toolbar_favorite_active.svg')
      : imgPath('spaces_toolbar_favorite.svg');

    return(
      <div style={{backgroundColor: TabStyles.Colors.ApplicationBlue, border: 'none', cursor: 'pointer', minHeight: 0, minWidth: 0}} data-tb-test-id={'favorites-dropdown-Button'}>
        <img
          style={{display:'block'}}
          src={imgSrc}
          width={24}
          height={24}
          title={this.closePanel ? Messages.favoritesMenuHide() : Messages.favoritesMenuShow()}
        />
      </div>
    );
  }
}

const DropdownContainerStyle: React.CSSProperties = {
  width: '100%',
  lineHeight: HeaderStyles.TopBarLineHeight,
  height: HeaderStyles.TopBarLineHeight,
  display: 'flex',
  alignItems: 'center',
};

export namespace FavoritesDropdownAngularProvider {
  interface AngularServices {
    readonly favorites: Favorites;
    readonly telemetry: Telemetry;
  }

  function getAngularServicesWithServiceLocator() {
    const favorites = ServiceLocator.get<Favorites>('Favorites');
    const telemetry = ServiceLocator.get<Telemetry>('TelemetryService');
    return {
      favorites,
      telemetry
    };
  }

  function selectPropsFromAngularServices(angularServices: AngularServices) {
    return {
      favoritesService: angularServices.favorites,
      telemetry: angularServices.telemetry
    };
  }

  export const HOC = angularProviderHOC<FavoritesDropdownOwnProps, FavoritesDropdownPropsFromAngular, AngularServices>({
    getAngularServicesWithServiceLocator,
    selectPropsFromAngularServices,
  });
}

export const PageHeaderFavoritesDropdown: React.ComponentType<FavoritesDropdownOwnProps> =
  FavoritesDropdownAngularProvider.HOC(FavoritesDropdown);
